/**
 * 役种判定用的通用检查器
 *
 * 这些函数被各个役种判定复用，是整个役种系统的基石。
 */

import {
  Counts,
  COPIES_PER_TILE,
  NUM_TILE_TYPES,
  indexToName,
  isHonor,
  isTerminal,
  isYaochu,
} from '../tile.js';
import type { Decomposition, Mentsu } from '../agari.js';

// ════════════════════════════════════════════════════════════════
// 基础属性
// ════════════════════════════════════════════════════════════════

/** 面子是否为顺子 */
export function isShuntsu(m: Mentsu): boolean {
  return m.kind === 'shuntsu';
}

/** 面子是否为刻子（含杠子） */
export function isKoutsu(m: Mentsu): boolean {
  return m.kind === 'koutsu' || m.kind === 'kantsu';
}

/** 面子是否为杠子 */
export function isKantsu(m: Mentsu): boolean {
  return m.kind === 'kantsu';
}

/** 面子是否暗刻（刻子/杠子且未副露） */
export function isAnkou(m: Mentsu): boolean {
  return isKoutsu(m) && !m.open;
}

/** 面子包含的所有牌索引 */
export function mentsuTiles(m: Mentsu): number[] {
  if (m.kind === 'shuntsu') return [m.start, m.start + 1, m.start + 2];
  const n = m.kind === 'kantsu' ? 4 : 3;
  return Array.from({ length: n }, () => m.start);
}

/** 面子是否含幺九牌 */
export function mentsuHasYaochu(m: Mentsu): boolean {
  return mentsuTiles(m).some(isYaochu);
}

/** 面子是否全为幺九牌（刻子类） */
export function mentsuAllYaochu(m: Mentsu): boolean {
  return mentsuTiles(m).every(isYaochu);
}

/** 面子是否含老头牌（1/9 数牌，不含字牌） */
export function mentsuHasTerminal(m: Mentsu): boolean {
  return mentsuTiles(m).some(isTerminal);
}

/** 面子是否全为老头牌 */
export function mentsuAllTerminal(m: Mentsu): boolean {
  return mentsuTiles(m).every(isTerminal);
}

/** 面子的花色（顺子=该花色；刻子=该牌花色；字牌返回 'z'） */
export function mentsuSuit(m: Mentsu): 'm' | 'p' | 's' | 'z' {
  const i = m.start;
  if (i < 9) return 'm';
  if (i < 18) return 'p';
  if (i < 27) return 's';
  return 'z';
}

/** 顺子的起始牌面（1-9） */
export function shuntsuStartFace(m: Mentsu): number {
  return (m.start % 9) + 1;
}

// ════════════════════════════════════════════════════════════════
// 牌型役检查
// ════════════════════════════════════════════════════════════════

/**
 * 平和（Pinfu）
 * 条件（全部门清）：
 *   1. 4 个面子全是顺子
 *   2. 雀头不是役牌（场风/自风/三元牌）
 *   3. 听牌型是两面听
 *
 * @param isRyanmenWait 是否两面听（由调用方传入）
 * @param pairTile 雀头牌索引
 * @param yakuhaiTiles 役牌索引集合
 */
export function checkPinfu(
  d: Decomposition,
  isRyanmenWait: boolean,
  pairTile: number | null,
  yakuhaiTiles: readonly number[],
): boolean {
  if (pairTile === null) return false;
  // 必须全顺子
  if (!d.mentsu.every(isShuntsu)) return false;
  // 雀头不能是役牌
  if (yakuhaiTiles.includes(pairTile)) return false;
  // 必须两面听
  return isRyanmenWait;
}

/**
 * 断幺九（Tanyao）
 * 所有牌都是中张（2-8 数牌），无幺九牌、无字牌
 */
export function checkTanyao(allTiles: Counts): boolean {
  for (let i = 0; i < NUM_TILE_TYPES; i++) {
    if (allTiles[i] > 0 && isYaochu(i)) return false;
  }
  return true;
}

/**
 * 役牌（Yakuhai）
 * 三元牌 / 场风 / 自风的刻子（或杠子）
 * @returns 命中的役牌索引列表
 */
export function findYakuhai(
  d: Decomposition,
  roundWind: number,
  seatWind: number,
): number[] {
  const dragons = [31, 32, 33]; // 白发中
  const result: number[] = [];
  for (const m of d.mentsu) {
    if (!isKoutsu(m)) continue;
    const t = m.start;
    if (dragons.includes(t) || t === roundWind || t === seatWind) {
      result.push(t);
    }
  }
  return result;
}

/**
 * 混一色（Honitsu）/ 清一色（Chinitsu）
 * @returns 'chinitsu' | 'honitsu' | null
 */
export function checkFlush(allTiles: Counts): 'chinitsu' | 'honitsu' | null {
  let hasHonor = false;
  const suits = new Set<number>();
  for (let i = 0; i < NUM_TILE_TYPES; i++) {
    if (allTiles[i] === 0) continue;
    if (isHonor(i)) {
      hasHonor = true;
    } else {
      suits.add(Math.floor(i / 9));
    }
  }
  if (suits.size === 0) return null; // 全字牌（字一色，另算）
  if (suits.size > 1) return null;
  return hasHonor ? 'honitsu' : 'chinitsu';
}

/**
 * 对对和（Toitoi）
 * 4 个面子全是刻子（或杠子）
 */
export function checkToitoi(d: Decomposition): boolean {
  return d.mentsu.every(isKoutsu);
}

/**
 * 三暗刻 / 四暗刻
 * 数暗刻的数量（含暗杠）
 * 注意：荣和时，和了牌所在的刻子算明刻（除非是暗杠）
 */
export function countAnkou(d: Decomposition, winTile: number, isTsumo: boolean): number {
  let count = 0;
  for (const m of d.mentsu) {
    if (!isAnkou(m)) continue;
    // 荣和时，和了牌凑成的刻子不算暗刻
    if (!isTsumo && isKoutsu(m) && m.start === winTile) {
      // 只有当和了牌确实用于这个刻子时才降为明刻
      continue;
    }
    count++;
  }
  return count;
}

/**
 * 一杯口 / 二杯口
 * 一杯口：两个完全相同的顺子（门清限定）
 * 二杯口：两组一杯口（门清限定）
 * @returns 相同顺子的组数（2 = 一杯口，4 = 二杯口）
 */
export function countIipeikou(d: Decomposition): number {
  const shuntsuStarts: number[] = [];
  for (const m of d.mentsu) {
    if (isShuntsu(m)) shuntsuStarts.push(m.start);
  }
  // 统计每种顺子出现的次数
  const counter = new Map<number, number>();
  for (const s of shuntsuStarts) counter.set(s, (counter.get(s) ?? 0) + 1);

  let pairs = 0;
  for (const [, n] of counter) {
    if (n >= 2) pairs += Math.floor(n / 2) * 2;
  }
  return pairs;
}

/**
 * 三色同顺（Sanshoku Doujun）
 * 三个花色中有相同数字的顺子
 */
export function checkSanshokuDoujun(d: Decomposition): boolean {
  const byFace = new Map<number, Set<string>>();
  for (const m of d.mentsu) {
    if (!isShuntsu(m)) continue;
    const face = shuntsuStartFace(m);
    const suit = mentsuSuit(m);
    if (!byFace.has(face)) byFace.set(face, new Set());
    byFace.get(face)!.add(suit);
  }
  for (const [, suits] of byFace) {
    if (suits.has('m') && suits.has('p') && suits.has('s')) return true;
  }
  return false;
}

/**
 * 三色同刻（Sanshoku Doukou）
 * 三个花色中有相同数字的刻子
 */
export function checkSanshokuDoukou(d: Decomposition): boolean {
  const byTile = new Map<number, Set<string>>();
  for (const m of d.mentsu) {
    if (!isKoutsu(m)) continue;
    const face = (m.start % 9) + 1;
    const suit = mentsuSuit(m);
    if (suit === 'z') continue;
    if (!byTile.has(face)) byTile.set(face, new Set());
    byTile.get(face)!.add(suit);
  }
  for (const [, suits] of byTile) {
    if (suits.has('m') && suits.has('p') && suits.has('s')) return true;
  }
  return false;
}

/**
 * 一气通贯（Ittsu / Chuuren）
 * 同一花色有 123 + 456 + 789 三个顺子
 */
export function checkIttsu(d: Decomposition): boolean {
  const bySuit = new Map<string, Set<number>>();
  for (const m of d.mentsu) {
    if (!isShuntsu(m)) continue;
    const suit = mentsuSuit(m);
    const face = shuntsuStartFace(m);
    if (!bySuit.has(suit)) bySuit.set(suit, new Set());
    bySuit.get(suit)!.add(face);
  }
  for (const [, faces] of bySuit) {
    if (faces.has(1) && faces.has(4) && faces.has(7)) return true;
  }
  return false;
}

/**
 * 全带幺九 / 纯全带幺九 / 混老头
 * 混全带幺九（Chanta）：每个面子（含雀头）都含幺九牌，且有字牌
 * 纯全带幺九（Junchan）：每个面子都含老头牌（1/9），且无字牌
 * 混老头（Honroutou）：全部由幺九牌组成（无顺子）
 */
export function checkChanta(
  d: Decomposition,
  allTiles: Counts,
): 'chanta' | 'junchan' | 'honroutou' | null {
  if (d.pair === null) return null;
  const pair = d.pair;

  // 每个面子和雀头都要含幺九
  const allHaveYaochu = d.mentsu.every(mentsuHasYaochu) && isYaochu(pair);
  if (!allHaveYaochu) return null;

  // 是否全是刻子且全幺九 → 混老头
  const allKoutsu = d.mentsu.every(isKoutsu);
  const allYaochuTiles = (() => {
    for (let i = 0; i < NUM_TILE_TYPES; i++) {
      if (allTiles[i] > 0 && !isYaochu(i)) return false;
    }
    return true;
  })();
  if (allKoutsu && allYaochuTiles) return 'honroutou';

  // 是否有字牌
  let hasHonor = false;
  for (let i = 27; i < NUM_TILE_TYPES; i++) {
    if (allTiles[i] > 0) hasHonor = true;
  }
  return hasHonor ? 'chanta' : 'junchan';
}

/**
 * 小三元 / 大三元
 * 大三元：白发中三个刻子
 * 小三元：白发中其中两个是刻子，另一个是雀头
 */
export function checkDragons(
  d: Decomposition,
): 'daisangen' | 'shousangen' | null {
  const dragons = [31, 32, 33];
  let koutsuCount = 0;
  for (const m of d.mentsu) {
    if (isKoutsu(m) && dragons.includes(m.start)) koutsuCount++;
  }
  if (koutsuCount === 3) return 'daisangen';
  if (koutsuCount === 2 && d.pair !== null && dragons.includes(d.pair)) {
    return 'shousangen';
  }
  return null;
}

/**
 * 风牌役：大四喜 / 小四喜
 */
export function checkWinds(d: Decomposition): 'daisuushii' | 'shousuushii' | null {
  const winds = [27, 28, 29, 30];
  let koutsuCount = 0;
  for (const m of d.mentsu) {
    if (isKoutsu(m) && winds.includes(m.start)) koutsuCount++;
  }
  if (koutsuCount === 4) return 'daisuushii';
  if (koutsuCount === 3 && d.pair !== null && winds.includes(d.pair)) {
    return 'shousuushii';
  }
  return null;
}

/**
 * 三杠子 / 四杠子
 */
export function countKantsu(d: Decomposition): number {
  return d.mentsu.filter(isKantsu).length;
}

/**
 * 字一色：全部是字牌
 */
export function checkTsuuiisou(allTiles: Counts): boolean {
  for (let i = 0; i < 27; i++) {
    if (allTiles[i] > 0) return false;
  }
  let hasHonor = false;
  for (let i = 27; i < NUM_TILE_TYPES; i++) if (allTiles[i] > 0) hasHonor = true;
  return hasHonor;
}

/**
 * 清老头：全部是老头牌（1/9，无字牌）
 */
export function checkChinroutou(allTiles: Counts): boolean {
  for (let i = 0; i < NUM_TILE_TYPES; i++) {
    if (allTiles[i] === 0) continue;
    if (!isTerminal(i)) return false;
  }
  return true;
}

/**
 * 绿一色：只含 2s/3s/4s/6s/8s/发
 * 绿牌索引：2s=19, 3s=20, 4s=21, 6s=23, 8s=25, 发=32
 */
const GREEN_TILES = [19, 20, 21, 23, 25, 32];
export function checkRyuuiisou(allTiles: Counts): boolean {
  for (let i = 0; i < NUM_TILE_TYPES; i++) {
    if (allTiles[i] > 0 && !GREEN_TILES.includes(i)) return false;
  }
  // 至少要有一张绿牌
  return GREEN_TILES.some((t) => allTiles[t] > 0);
}

/**
 * 九莲宝灯（Chuuren Poutou）
 * 门清 + 单一花色 + 1112345678999 的形态（多一张任意同花色牌）
 */
export function checkChuuren(allTiles: Counts): boolean {
  // 必须单一花色（无字牌）
  let suit = -1;
  for (let i = 0; i < NUM_TILE_TYPES; i++) {
    if (allTiles[i] === 0) continue;
    if (isHonor(i)) return false;
    const s = Math.floor(i / 9);
    if (suit === -1) suit = s;
    else if (suit !== s) return false;
  }
  if (suit === -1) return false;

  const base = suit * 9;
  // 基础形：1×3, 2-8 各 1, 9×3
  const required = [3, 1, 1, 1, 1, 1, 1, 1, 3];
  let extra = 0;
  for (let i = 0; i < 9; i++) {
    const have = allTiles[base + i];
    const need = required[i];
    if (have < need) return false;
    extra += have - need;
  }
  // 恰好多 1 张（14 张）
  return extra === 1;
}

/** 九莲宝灯的纯正形态（和了牌是那"多出来的一张"，即九面听） */
export function checkJunseiChuuren(
  allTiles: Counts,
  winTile: number,
  beforeCounts: Counts,
): boolean {
  if (!checkChuuren(allTiles)) return false;
  // 纯正：和牌前已经是 1112345678999 的完整 13 张
  let suit = -1;
  for (let i = 0; i < NUM_TILE_TYPES; i++) {
    if (beforeCounts[i] === 0) continue;
    suit = Math.floor(i / 9);
    break;
  }
  const base = suit * 9;
  const required = [3, 1, 1, 1, 1, 1, 1, 1, 3];
  for (let i = 0; i < 9; i++) {
    if (beforeCounts[base + i] !== required[i]) return false;
  }
  return winTile >= base && winTile < base + 9;
}

/**
 * 四杠子（Suukantsu）
 */
export function checkSuukantsu(d: Decomposition): boolean {
  return countKantsu(d) === 4;
}

/**
 * 国士无双相关（已和判定见 agari.ts）
 * 判定是否为十三面（纯正国士）
 */
export function isKokushi13(beforeCounts: Counts): boolean {
  const yaochu = [0, 8, 9, 17, 18, 26, 27, 28, 29, 30, 31, 32, 33];
  // 和牌前：13 种幺九各 1 张
  return yaochu.every((t) => beforeCounts[t] === 1);
}

// ════════════════════════════════════════════════════════════════
// 宝牌计算
// ════════════════════════════════════════════════════════════════

/**
 * 由指示牌计算宝牌
 * 数牌：n → n+1（9 → 1）
 * 风牌：东南西北→东（1→2→3→4→1）
 * 三元牌：白发中→白（5→6→7→5）
 */
export function doraFromIndicator(indicator: number): number {
  if (isHonor(indicator)) {
    if (indicator <= 30) {
      // 风牌 27-30
      return indicator === 30 ? 27 : indicator + 1;
    }
    // 三元牌 31-33
    return indicator === 33 ? 31 : indicator + 1;
  }
  const num = indicator % 9; // 0-8
  const base = indicator - num;
  return base + ((num + 1) % 9);
}

/**
 * 计算宝牌数量
 * @param allTiles 全部牌（含副露）
 * @param indicators 指示牌列表
 */
export function countDora(allTiles: Counts, indicators: readonly number[]): number {
  let count = 0;
  for (const ind of indicators) {
    const dora = doraFromIndicator(ind);
    count += allTiles[dora];
  }
  return count;
}

/** 计算赤宝牌数量（简化：统计 5m/5p/5s，需要外部告知赤牌数量） */
export function countRedFives(allTiles: Counts, redCount: number): number {
  return redCount;
}
